// Actionable remediation content used by the in-app "How to fix" guides.

export const CSP_VALUE =
  "default-src 'self'; script-src 'self' https://js.stripe.com; frame-src https://js.stripe.com https://hooks.stripe.com; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; connect-src 'self' https://api.stripe.com https://linkwebsitepro.linklitepro.com; img-src 'self' data: https:; frame-ancestors 'none'; base-uri 'self'; form-action 'self';";

// The six headers the Security score is built from (key must match the scan `security` object).
export const SECURITY_HEADERS = [
  { key: "hsts", name: "Strict-Transport-Security", value: "max-age=31536000; includeSubDomains; preload", points: 20 },
  { key: "csp", name: "Content-Security-Policy", value: CSP_VALUE, points: 20 },
  { key: "xFrameOptions", name: "X-Frame-Options", value: "DENY", points: 15 },
  { key: "xContentTypeOptions", name: "X-Content-Type-Options", value: "nosniff", points: 10 },
  { key: "referrerPolicy", name: "Referrer-Policy", value: "strict-origin-when-cross-origin", points: 10 },
  { key: "permissionsPolicy", name: "Permissions-Policy", value: "camera=(), microphone=(), geolocation=()", points: 5 },
];

// Where to set HTTP response headers (works for the site's root document).
export const CLOUDFLARE_STEPS = [
  "Log in to dash.cloudflare.com and open your site (zone).",
  "Left sidebar → Rules → Transform Rules → open the “Modify Response Header” tab.",
  "Click Create rule → choose “Response Header Transform Rule”.",
  "Set “When incoming requests match” → All incoming requests.",
  "Under “Then”, add each header with operation “Set static”: paste only the header name in the Name box and the value in the Value box.",
  "Click Deploy, then re-scan this domain — the Security score updates immediately.",
];

// flag id -> { steps?, code?, codeLabel? }
export const FIX_GUIDES = {
  "missing-csp": {
    codeLabel: "Response header",
    code: `Content-Security-Policy: ${CSP_VALUE}`,
    steps: CLOUDFLARE_STEPS,
  },
  "missing-hsts": {
    codeLabel: "Response header",
    code: "Strict-Transport-Security: max-age=31536000; includeSubDomains; preload",
    steps: CLOUDFLARE_STEPS,
  },
  "no-https": {
    steps: [
      "Install a valid TLS/SSL certificate (Cloudflare offers free Universal SSL).",
      "Force HTTPS site-wide with a 301 redirect from http → https.",
      "Then add the Strict-Transport-Security (HSTS) header.",
    ],
  },
  "insecure-form-post": {
    codeLabel: "Change every form action to HTTPS",
    code: '<form action="https://yourdomain.com/submit" method="post"> … </form>',
  },
  "missing-privacy-policy": {
    steps: ["Publish a privacy policy page describing what data you collect and why.", "Link it in your site footer."],
    codeLabel: "Footer link",
    code: '<a href="/privacy">Privacy Policy</a>',
  },
  "no-privacy-link": {
    codeLabel: "Footer link",
    code: '<a href="/privacy">Privacy Policy</a>',
  },
  "missing-cookie-consent": {
    steps: [
      "Add a consent manager (e.g., Cookiebot or OneTrust).",
      "Block non-essential trackers until the visitor consents.",
    ],
  },
  "seo-no-title": { codeLabel: "Add to <head>", code: "<title>Primary Keyword — Brand Name</title>" },
  "seo-no-description": {
    codeLabel: "Add to <head>",
    code: '<meta name="description" content="A compelling ~150-character summary of this page." />',
  },
  "seo-no-h1": { codeLabel: "Add one H1 to the page", code: "<h1>Clear, descriptive page heading</h1>" },
  "seo-no-canonical": {
    codeLabel: "Add to <head>",
    code: '<link rel="canonical" href="https://yourdomain.com/this-page" />',
  },
  "seo-no-lang": { codeLabel: "Set on the html element", code: '<html lang="en">' },
  "seo-no-open-graph": {
    codeLabel: "Add to <head>",
    code: `<meta property="og:title" content="Page title" />
<meta property="og:description" content="Short description" />
<meta property="og:image" content="https://yourdomain.com/preview.png" />
<meta property="og:type" content="website" />`,
  },
  "seo-no-structured-data": {
    codeLabel: "Add before </body> (JSON-LD)",
    code: `<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Your Brand",
  "url": "https://yourdomain.com",
  "logo": "https://yourdomain.com/logo.png"
}
</script>`,
  },
  "seo-no-robots-txt": {
    codeLabel: "Create /robots.txt",
    code: `User-agent: *
Allow: /
Sitemap: https://yourdomain.com/sitemap.xml`,
  },
  "seo-no-sitemap": {
    codeLabel: "Create /sitemap.xml",
    code: `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url><loc>https://yourdomain.com/</loc></url>
</urlset>`,
  },
};
